/**
 * Minimal React admin route registry for runtime (App shell, nav, notifications).
 * Keep id + route in sync with REACT_ADMIN_PAGES in react-admin-pages.ts (parity/docs metadata).
 */
export type ReactAdminPageId =
  | 'reports'
  | 'sites'
  | 'operator-notices'
  | 'contacts'
  | 'wizard'
  | 'sops'
  | 'operator-simulator'
  | 'retention-hub'
  | 'technologies'
  | 'workflows'
  | 'stats'
  | 'users'
  | 'templates'
  | 'system-tasks'
  | 'ai-profiles'
  | 'orchestrator'
  | 'report-delivery'
  | 'delivered-reports'
  | 'roles'
  | 'settings'
  | 'system-logs'
  | 'webhook-monitor'
  | 'notifications'
  | 'application-stats'
  | 'user-analytics'
  | 'dealer-portal'
  | 'tools'
  | 'bug-reports'
  | 'account'
  | 'operator-portal'
  | 'operator-grading-config'
  | 'operator-grading-review'
  | 'operator-performance'
  | 'report-verification';

export type ReactAdminRouteEntry = {
  id: ReactAdminPageId;
  route: string;
};

/** Catalog href values served by the React admin SPA. */
export const REACT_ADMIN_ROUTE_MAP: readonly ReactAdminRouteEntry[] = [
  { id: 'reports', route: '/report-admin.html' },
  { id: 'sites', route: '/sites-admin.html' },
  { id: 'operator-notices', route: '/operator-notices.html' },
  { id: 'wizard', route: '/wizard.html' },
  { id: 'operator-simulator', route: '/operator-simulator.html' },
  { id: 'sops', route: '/sops.html' },
  { id: 'retention-hub', route: '/retention-hub.html' },
  { id: 'technologies', route: '/technologies.html' },
  { id: 'workflows', route: '/orchestrator-workflows.html' },
  { id: 'stats', route: '/stats.html' },
  { id: 'users', route: '/users.html' },
  { id: 'templates', route: '/templates.html' },
  { id: 'orchestrator', route: '/orchestrator.html' },
  { id: 'system-tasks', route: '/system-tasks.html' },
  { id: 'system-logs', route: '/system-logs.html' },
  { id: 'webhook-monitor', route: '/webhook-monitor.html' },
  { id: 'notifications', route: '/notifications.html' },
  { id: 'application-stats', route: '/application-stats.html' },
  { id: 'user-analytics', route: '/user-analytics.html' },
  { id: 'ai-profiles', route: '/ai-profiles.html' },
  { id: 'report-delivery', route: '/report-delivery.html' },
  { id: 'delivered-reports', route: '/delivered-reports.html' },
  { id: 'roles', route: '/roles.html' },
  { id: 'settings', route: '/settings.html' },
  { id: 'dealer-portal', route: '/dealer-portal-admin.html' },
  { id: 'contacts', route: '/contacts.html' },
  { id: 'tools', route: '/tools.html' },
  { id: 'bug-reports', route: '/bug-reports.html' },
  { id: 'account', route: '/account.html' },
  { id: 'operator-grading-config', route: '/operator-grading-config.html' },
  { id: 'operator-grading-review', route: '/operator-grading-review.html' },
  { id: 'operator-performance', route: '/operator-performance.html' },
  { id: 'report-verification', route: '/report-verification.html' },
  { id: 'operator-portal', route: '/operator-portal.html' },
] as const;

export const REACT_ADMIN_ROUTE_HREFS = new Set(
  REACT_ADMIN_ROUTE_MAP.map((page) => page.route.replace(/^\//, '')),
);

export function isReactAdminHref(href: string): boolean {
  return REACT_ADMIN_ROUTE_HREFS.has(href.replace(/^\/+/, ''));
}

export function reactNavHrefForRouteHref(routeHref: string): string {
  const normalized = routeHref.replace(/^\/+/, '');
  return `/${normalized}`;
}

export function getReactAdminPage(pathname: string): ReactAdminRouteEntry {
  const normalizedPath = pathname.replace(/\/+$/, '') || '/report-admin.html';
  return (
    REACT_ADMIN_ROUTE_MAP.find((page) => page.route === normalizedPath) ?? REACT_ADMIN_ROUTE_MAP[0]
  );
}
